/**
 * <faq-tabs>
 *
 * Accessible tablist with arrow-key nav + per-tab accordion items.
 * Each tab button has role="tab" + aria-selected.
 * Each panel has role="tabpanel" + aria-hidden when inactive.
 * Inside a panel, <details>/<summary> handle the per-question expand/collapse natively.
 */
(() => {
  if (customElements.get('faq-tabs')) return;

  class FaqTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      // Mobile filter dropdown (Figma mobile spec) — null on desktop layouts
      // that don't render the mobile-bar markup.
      this.tablist = this.querySelector('.salt-faq__tablist');
      this.currentLabel = this.querySelector('[data-role="current-label"]');
      this.filterToggle = this.querySelector('[data-role="filter-toggle"]');
      if (this.tabs.length === 0) return;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => this.handleKeydown(e));
      });

      if (this.filterToggle && this.tablist) {
        this.filterToggle.addEventListener('click', () => this.toggleDropdown());
        // Click-outside to close on mobile.
        document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) this.closeDropdown();
        });
      }

      // Activate the first tab marked aria-selected="true", or default to first.
      const initial = this.tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
      this.select(initial >= 0 ? initial : 0);
    }

    select(index) {
      const n = this.tabs.length;
      this.activeIndex = ((index % n) + n) % n;
      this.tabs.forEach((tab, i) => {
        const isActive = i === this.activeIndex;
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });
      this.panels.forEach((panel, i) => {
        const isActive = i === this.activeIndex;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
      });
      // Mirror the active tab's label into the mobile bar and collapse the
      // dropdown after a selection.
      if (this.currentLabel) {
        const activeTab = this.tabs[this.activeIndex];
        if (activeTab) this.currentLabel.textContent = activeTab.textContent.trim();
      }
      this.closeDropdown();
    }

    toggleDropdown() {
      if (!this.filterToggle || !this.tablist) return;
      const expanded = this.filterToggle.getAttribute('aria-expanded') === 'true';
      this.filterToggle.setAttribute('aria-expanded', String(!expanded));
      this.tablist.dataset.expanded = String(!expanded);
    }

    closeDropdown() {
      if (!this.filterToggle || !this.tablist) return;
      this.filterToggle.setAttribute('aria-expanded', 'false');
      this.tablist.dataset.expanded = 'false';
    }

    handleKeydown(e) {
      const { key } = e;
      const currentIndex = this.tabs.findIndex(t => t === document.activeElement);
      if (currentIndex < 0) return;
      let nextIndex = currentIndex;
      if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      else if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      else if (key === 'Home') nextIndex = 0;
      else if (key === 'End') nextIndex = this.tabs.length - 1;
      else return;
      e.preventDefault();
      this.tabs[nextIndex].focus();
      this.select(nextIndex);
    }
  }

  customElements.define('faq-tabs', FaqTabs);
})();
